import React, { useMemo, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    CircularProgress,
    Link,
    Box,
    Typography,
    Collapse,
} from '@mui/material'
import { truncate, get, isEmpty } from 'lodash-es'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { ArrowRight, ArrowDropDown, FileCopy } from '@mui/icons-material'
import { JsonFormsWrapper, getDynamicConfigSchema } from '@thefront/pandipackV2'
import { Capitalize, CONNECTOR_ERROR_MESSAGES } from '@thefront/pandipackV2'
import { POPUP_WINDOW_PARAMS, useThemeConfig } from '../../themeConfig'
import { ContentBlock } from '../common'
import { sendPandiumEvent, PANDIUM_EVENTS } from '../../utils/postMessage'

// format a string with placeholders like {{ key }}
const formatString = (string, context) => {
    return string.replace(/{{\s*([\w.]+)\s*}}/g, (_, key) => {
        const value = get(context, key, '')
        // only return string or number values
        return ['string', 'number'].includes(typeof value) ? value : ''
    })
}

const ConnectDialog = ({
    close,
    connector,
    tenant,
    oAuth2Link,
    modalError,
    modalLoading,
    open,
    connectBasic,
    connectRequest,
    mode, // 'internal' (IMP) or 'external' (EMP)
    datevClients = null,
    authStatus = null,
    onStartOver,
}) => {
    const theme = useThemeConfig()
    const [connectorStep, setConnectorStep] = useState(1)

    // Datev OAuth: tracks whether the user has submitted the post-OAuth client selection
    const [selectionSubmitted, setSelectionSubmitted] = useState(false)
    const isDatev = connector?.name === 'datev_oauth'
    const inSelectionPhase =
        isDatev && authStatus === 'Pending second OAuth' && !selectionSubmitted

    const save = (connectorName, values) => {
        connectBasic(connectorName, values)
        if (!connector.metadata?.test_after_connect) {
            close()
        }
    }
    const next = (connectorName, values) => {
        connectRequest(connectorName, values)
        setConnectorStep(2)
    }
    const handleSubmitSelection = () => {
        const sourceKey = get(connector, 'jsonrender.options_source')
        const selected = secretValues?.[sourceKey] || {}
        connectRequest(connector.name, selected, { oauth_step: 2 })
        setSelectionSubmitted(true)
    }

    const [secretValues, setSecretValues] = useState()
    const helpText = get(connector, 'helpText', false)
    const shouldRenderForm =
        ['backend', 'jwt', 'legacy'].includes(
            get(connector, 'metadata.connection_flow')
        ) ||
        ['ezcom', 'field_nation', 'sage_intacct', 'mindbody'].includes(
            connector.name
        ) ||
        connector.type === 'basic'

    const [isJsonFormValid, setIsJsonFormValid] = useState(false)
    const [showErrorDetails, setShowErrorDetails] = useState(false)
    const onJsonFormChange = (errors, jsonFormValues) => {
        setSecretValues(jsonFormValues)
        isEmpty(errors) ? setIsJsonFormValid(true) : setIsJsonFormValid(false)
    }

    // Reshape the Datev clients list into dynamic JSON Forms options.
    const dynamicConfigs = useMemo(() => {
        if (!inSelectionPhase) return null
        const jr = get(connector, 'jsonrender', {})
        const formatLabel = (opt) => {
            const fmt = jr.options_label_format
            if (typeof fmt === 'string') {
                return fmt.replace(/\{(\w+)\}/g, (_, key) =>
                    String(opt?.[key] ?? '')
                )
            }
            return String(opt?.[jr.options_label_field] ?? '')
        }
        const options = (datevClients || []).map((opt) => ({
            const: Object.fromEntries(
                (jr.options_value_fields || []).map((f) => [f, opt[f]])
            ),
            title: formatLabel(opt),
        }))
        return { [jr.options_source]: options }
    }, [inSelectionPhase, connector, datevClients])

    const selectionSchema = useMemo(() => {
        if (!inSelectionPhase) return null
        return getDynamicConfigSchema(
            get(connector, 'jsonrender.schema'),
            dynamicConfigs
        )
    }, [inSelectionPhase, connector, dynamicConfigs])

    const templateContext = {
        integration_name: tenant.integration.name,
        integration_long_name: tenant.integration.long_name,
        integration_partner_name: tenant.integration.partner?.name,
        integration_documentation_url:
            tenant.integration.marketplace_settings?.docURL,
        connector_label: connector.label ?? connector.name,
    }

    let authDialogText
    const overrideText =
        mode === 'external' &&
        (tenant.integration.marketplace_settings?.authDialogText?.[
            connector.name
        ] ||
            theme.configs.marketplaceSettings.authDialogText)
    if (overrideText) {
        authDialogText = formatString(overrideText, templateContext)
    } else {
        authDialogText = `Thank you for your interest in the ${
            tenant.integration.long_name ?? tenant.integration.name
        } integration! To authenticate your account please enter in the following credentials.`
    }

    // Configurable dialog title with fallback to default
    const overrideTitle =
        tenant.integration.marketplace_settings?.authDialogTitle?.[
            connector.name
        ] || theme.configs.marketplaceSettings?.authDialogTitle
    const authDialogTitle = overrideTitle
        ? formatString(overrideTitle, templateContext)
        : `${connector.label || Capitalize(connector.name)} Authentication`

    // Configurable error message with fallback to default
    const overrideErrorMessage =
        tenant.integration.marketplace_settings?.errorMessage?.[
            connector.name
        ] || theme.configs.marketplaceSettings?.errorMessage
    const errorMessage = overrideErrorMessage
        ? formatString(overrideErrorMessage, templateContext)
        : CONNECTOR_ERROR_MESSAGES.authFailed

    // OAuth-link block used by both the Datev two-step and normal OAuth2 flows.
    const renderOAuthLink = () =>
        oAuth2Link ? (
            <div>
                <DialogContentText>
                    Follow the link below to complete the connection process.
                </DialogContentText>
                <CopyToClipboard text={oAuth2Link}>
                    <IconButton size="large">
                        <FileCopy />
                    </IconButton>
                </CopyToClipboard>
                <Link
                    href={oAuth2Link}
                    onClick={(e) => {
                        window.open(oAuth2Link, null, POPUP_WINDOW_PARAMS)
                        e.preventDefault()
                    }}
                    sx={{ color: '#1976d2' }}
                    underline="hover"
                >
                    {' '}
                    {truncate(oAuth2Link, { length: 40 })}{' '}
                </Link>
            </div>
        ) : (
            <Box sx={{ textAlign: 'center', mt: '40px' }}>
                <CircularProgress />
            </Box>
        )

    const connectDialogContents = (
        <>
            <DialogContent
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                }}
            >
                {helpText && !overrideText && (
                    <DialogContentText sx={{ mb: '8px' }}>
                        {helpText}
                    </DialogContentText>
                )}
                {modalError && (
                    <>
                        <ContentBlock
                            record={{ text: errorMessage }}
                            source="text"
                            sx={{ mb: '8px', color: 'error.main' }}
                        />
                        <Button
                            sx={{
                                width: '35px',
                                mb: '8px',
                            }}
                            color="error"
                            startIcon={
                                showErrorDetails ? (
                                    <ArrowDropDown />
                                ) : (
                                    <ArrowRight />
                                )
                            }
                            onClick={() =>
                                setShowErrorDetails(!showErrorDetails)
                            }
                            size="small"
                        >
                            Details
                        </Button>
                        <Collapse in={showErrorDetails}>
                            <DialogContentText
                                sx={{ margin: '-4px 0px 8px 12px' }}
                                color="error"
                            >
                                {modalError}
                            </DialogContentText>
                        </Collapse>
                    </>
                )}
                {isDatev ? (
                    inSelectionPhase ? (
                        <JsonFormsWrapper
                            schema={selectionSchema}
                            uischema={get(connector, 'jsonrender.uischema')}
                            data={secretValues}
                            onJsonFormChange={onJsonFormChange}
                            dynamicConfigs={dynamicConfigs}
                            fullWidth={true}
                            mode={mode}
                        />
                    ) : (
                        renderOAuthLink()
                    )
                ) : connector.metadata?.test_after_connect && modalLoading ? (
                    <Box sx={{ margin: 'auto' }}>
                        <CircularProgress />
                    </Box>
                ) : // Content differs based on connector type
                shouldRenderForm && !get(connector, 'jsonrender.schema') ? (
                    <>
                        {mode === 'external' &&
                            !modalError &&
                            (!helpText || overrideText) && (
                                <Typography>
                                    <ContentBlock
                                        record={{ text: authDialogText }}
                                        source="text"
                                        variant="body1"
                                    />
                                </Typography>
                            )}
                        <JsonFormsWrapper
                            schema={get(connector, 'jsonform.schema')}
                            uischema={get(connector, 'jsonform.uischema')}
                            data={secretValues}
                            onJsonFormChange={onJsonFormChange}
                            fullWidth={true}
                            mode={mode}
                        />
                    </>
                ) : // Render content for oauth2
                (connector.type === 'oauth2' &&
                      get(connector, 'metadata.multi_step') !== true) ||
                  (get(connector, 'metadata.multi_step') === true &&
                      connectorStep === 2) ? (
                    // Render content for backend & legacy Oauth 2 connectors that take both tenant level secrets and integration level secrets
                    ['backend', 'legacy'].includes(
                        get(connector, 'metadata.connection_flow')
                    ) && get(connector, 'jsonrender.schema') ? (
                        <>
                            {mode === 'external' &&
                                !modalError &&
                                (!helpText || overrideText) && (
                                    <Typography>
                                        <ContentBlock
                                            record={{ text: authDialogText }}
                                            source="text"
                                            variant="body1"
                                        />
                                    </Typography>
                                )}
                            <JsonFormsWrapper
                                schema={get(connector, 'jsonrender.schema')}
                                uischema={get(connector, 'jsonrender.uischema')}
                                data={secretValues}
                                onJsonFormChange={onJsonFormChange}
                                fullWidth={true}
                                mode={mode}
                            />
                        </>
                    ) : // Render the Oauth 2 link for all other Oauth2 connectors
                    oAuth2Link ? (
                        <div>
                            <DialogContentText>
                                Follow the link below to complete the connection
                                process.
                            </DialogContentText>
                            <CopyToClipboard text={oAuth2Link}>
                                <IconButton size="large">
                                    <FileCopy />
                                </IconButton>
                            </CopyToClipboard>
                            <Link
                                href={oAuth2Link}
                                onClick={(e) => {
                                    window.open(
                                        oAuth2Link,
                                        null,
                                        POPUP_WINDOW_PARAMS
                                    )
                                    e.preventDefault()
                                }}
                                sx={{ color: '#1976d2' }}
                                underline="hover"
                            >
                                {' '}
                                {truncate(oAuth2Link, {
                                    length: 40,
                                })}{' '}
                            </Link>
                        </div>
                    ) : (
                        <Box
                            sx={{
                                textAlign: 'center',
                                mt: '40px',
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )
                ) : connector.name === 'linnworks' ? (
                    oAuth2Link ? (
                        <div>
                            <DialogContentText>
                                Please use the link below to generate your API
                                Key, and then pass that key into the form below.
                            </DialogContentText>
                            <CopyToClipboard text={oAuth2Link}>
                                <IconButton size="large">
                                    <FileCopy />
                                </IconButton>
                            </CopyToClipboard>
                            <Link
                                href={oAuth2Link}
                                onClick={(e) => {
                                    window.open(
                                        oAuth2Link,
                                        null,
                                        POPUP_WINDOW_PARAMS
                                    )
                                    e.preventDefault()
                                }}
                                sx={{ color: '#1976d2' }}
                                underline="hover"
                            >
                                {' '}
                                {truncate(oAuth2Link, {
                                    length: 40,
                                })}{' '}
                            </Link>
                            <JsonFormsWrapper
                                schema={get(connector, 'jsonrender.schema')}
                                uischema={get(connector, 'jsonrender.uischema')}
                                data={secretValues}
                                onJsonFormChange={onJsonFormChange}
                                fullWidth={true}
                                mode={mode}
                            />
                        </div>
                    ) : (
                        <Box
                            sx={{
                                textAlign: 'center',
                                mt: '40px',
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    )
                ) : get(connector, 'jsonrender.schema') ? (
                    <>
                        {mode === 'external' &&
                            !modalError &&
                            (!helpText || overrideText) && (
                                <Typography>
                                    <ContentBlock
                                        record={{ text: authDialogText }}
                                        source="text"
                                        variant="body1"
                                    />
                                </Typography>
                            )}
                        <JsonFormsWrapper
                            schema={get(connector, 'jsonrender.schema')}
                            uischema={get(connector, 'jsonrender.uischema')}
                            data={secretValues}
                            onJsonFormChange={onJsonFormChange}
                            fullWidth={true}
                            mode={mode}
                        />
                    </>
                ) : (
                    // For a different connector type?
                    <div> Under construction </div>
                )}
            </DialogContent>
            <DialogActions>
                {!modalLoading && (
                    <>
                        {inSelectionPhase && (
                            <Button
                                color="warning"
                                onClick={() => {
                                    onStartOver()
                                    close()
                                }}
                            >
                                Start over
                            </Button>
                        )}
                        {mode === 'internal' && (
                            <Button
                                onClick={() => {
                                    sendPandiumEvent(
                                        PANDIUM_EVENTS.connectorConnectCancel,
                                        { tenant }
                                    )
                                    close()
                                }}
                            >
                                Cancel
                            </Button>
                        )}
                        {inSelectionPhase && (
                            <Button
                                color="primary"
                                variant="contained"
                                disabled={!isJsonFormValid}
                                onClick={() => {
                                    sendPandiumEvent(
                                        PANDIUM_EVENTS.connectorConnectNext,
                                        { tenant }
                                    )
                                    handleSubmitSelection()
                                }}
                            >
                                Next
                            </Button>
                        )}
                        {!isDatev &&
                            (shouldRenderForm ||
                                connector.name === 'linnworks') && (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => {
                                        sendPandiumEvent(
                                            PANDIUM_EVENTS.connectorConnect,
                                            { tenant }
                                        )
                                        save(connector.name, secretValues)
                                        // connectBasicLoading()
                                    }}
                                    disabled={!isJsonFormValid}
                                >
                                    Connect
                                </Button>
                            )}
                        {!isDatev &&
                            get(connector, 'metadata.multi_step') === true &&
                            (connectorStep === 1 ? (
                                <Button
                                    onClick={() => {
                                        sendPandiumEvent(
                                            PANDIUM_EVENTS.connectorConnectNext,
                                            { tenant }
                                        )
                                        next(connector.name, secretValues)
                                    }}
                                >
                                    Next
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => {
                                        sendPandiumEvent(
                                            PANDIUM_EVENTS.connectorConnectBack,
                                            { tenant }
                                        )
                                        setConnectorStep(1)
                                    }}
                                >
                                    Back
                                </Button>
                            ))}
                    </>
                )}
            </DialogActions>
        </>
    )

    return mode === 'internal' ? (
        <Dialog
            open={open}
            maxWidth={'xs'}
            fullWidth={true}
            sx={{ padding: '20px' }}
            PaperProps={
                inSelectionPhase ? { sx: { overflow: 'visible' } } : undefined
            }
        >
            <DialogTitle sx={{ padding: '16px 24px 0px' }}>
                {authDialogTitle}
            </DialogTitle>
            {connectDialogContents}
        </Dialog>
    ) : (
        connectDialogContents
    )
}

export default ConnectDialog
